import React, { useMemo, useState } from "react";
import axios from "axios";
import { Alert, Button, Tag } from "antd";
import useLang from "../../../hooks/useLang";
import useBulkOrderOperation from "../../../hooks/useBulkOrderOperation";
import {
	BulkStatusUpdateError,
	BulkStatusUpdatePreflight,
	BulkStatusUpdateRequest,
	ResponsibleUser,
	executeBulkStatusUpdate,
	getResponsibleUsers,
	preflightBulkStatusUpdate,
} from "../../../api/bulk-order-operations";
import {
	MAX_ORDER_IDS,
	getCategoryConfig,
} from "../../../utils/bulkOperationsConfig";
import { parseOrderIds } from "../parseOrderIds";
import BulkOpsHeader from "../components/BulkOpsHeader";
import ConfirmModal from "../components/ConfirmModal";
import IdListSection from "../components/IdListSection";
import OrderIdsInput from "../components/OrderIdsInput";
import ResultPanels from "../components/ResultPanels";
import { PRIMARY_BUTTON_STYLE } from "../components/styles";
import StatusUpdateModal from "./StatusUpdateModal";
import { STATUS_UPDATE_OPTIONS } from "./statuses";

const StatusUpdates = () => {
	const { strings } = useLang("bulkOrderOperations");
	const common = strings?.common;
	const copy = strings?.statusUpdates;
	const categoryStrings = strings?.categories?.statusUpdates;

	const categoryConfig = getCategoryConfig("status-updates");
	// Single source of truth for the international system account — see the config file.
	const historyConfig = getCategoryConfig("history-management");

	const [rawInput, setRawInput] = useState("");
	const [checking, setChecking] = useState(false);
	const [preflight, setPreflight] =
		useState<BulkStatusUpdatePreflight | null>(null);
	const [preflightError, setPreflightError] =
		useState<BulkStatusUpdateError | null>(null);

	const [users, setUsers] = useState<ResponsibleUser[]>([]);
	const [usersLoading, setUsersLoading] = useState(false);

	const [modalOpen, setModalOpen] = useState(false);
	const [pending, setPending] = useState<BulkStatusUpdateRequest | null>(
		null
	);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [confirmWord, setConfirmWord] = useState("");

	const { submitting, result, errorResult, run, clearResults } =
		useBulkOrderOperation();

	const parsed = useMemo(() => parseOrderIds(rawInput), [rawInput]);
	const tooManyIds = parsed.valid.length > MAX_ORDER_IDS;

	// Editing the list invalidates the classification it produced — the next batch could be
	// a different kind entirely, and the modal's options depend on it.
	const changeInput = (value: string) => {
		setRawInput(value);
		setPreflight(null);
		setPreflightError(null);
		clearResults();
	};

	const handleCheck = async () => {
		setChecking(true);
		setPreflight(null);
		setPreflightError(null);
		clearResults();
		try {
			const data = await preflightBulkStatusUpdate(parsed.valid);
			setPreflight(data);

			// Only a local batch needs the staff list; an international one has its user forced.
			if (!data.isInternational) {
				setUsersLoading(true);
				try {
					setUsers(await getResponsibleUsers());
				} finally {
					setUsersLoading(false);
				}
			}
		} catch (err) {
			if (axios.isAxiosError(err) && err.response?.status === 400) {
				const data = err.response
					.data as Partial<BulkStatusUpdateError>;
				if (typeof data?.message === "string") {
					setPreflightError({
						message: data.message,
						groups: data.groups ?? [],
					});
				}
			}
		} finally {
			setChecking(false);
		}
	};

	const handleModalSubmit = (body: BulkStatusUpdateRequest) => {
		setPending(body);
		setModalOpen(false);
		setConfirmOpen(true);
	};

	const closeConfirm = () => {
		setConfirmOpen(false);
		setConfirmWord("");
	};

	const handleConfirm = async () => {
		if (!pending) return;
		await run(() => executeBulkStatusUpdate(pending));
		closeConfirm();
		setPending(null);
	};

	const directionLabel = preflight
		? copy?.[`direction${preflight.direction}`] ?? preflight.direction
		: "";

	const pendingStatus = pending
		? STATUS_UPDATE_OPTIONS.find((o) => o.target === pending.status)
		: null;

	const confirmDetail = pending
		? [
				copy?.[pendingStatus?.labelKey ?? ""] ??
					pendingStatus?.fallbackLabel ??
					pending.status,
				pendingStatus && preflight
					? pendingStatus.pairs[preflight.direction]
					: null,
				pending.writeHistory
					? copy?.confirmWithHistory ?? "+ history row"
					: copy?.confirmNoHistory ?? "no history row",
				// Only ever shown when it is ON — a reassignment of the delivery driver
				// must not be something the operator confirms without seeing it.
				pending.assignDeliveryDriver
					? copy?.confirmWithDriver ?? "+ delivery driver"
					: null,
		  ]
				.filter(Boolean)
				.join(" · ")
		: "";

	return (
		<div className="max-w-5xl mx-auto px-4 py-6 flex flex-col gap-6">
			<BulkOpsHeader
				title={categoryStrings?.label ?? "Bulk Status Updates"}
				subtitle={
					categoryStrings?.description ??
					"Correct the status of orders that did not get the right one"
				}
				backTo="/bulk-order-operations"
				backLabel={common?.back ?? "All categories"}
			/>

			{!categoryConfig && (
				<Alert
					type="error"
					showIcon
					message={
						common?.categoryUnavailable ??
						"This category is not configured for this country."
					}
				/>
			)}

			<OrderIdsInput
				value={rawInput}
				onChange={changeInput}
				parsed={parsed}
				tooManyIds={tooManyIds}
			/>

			<div>
				<Button
					size="large"
					style={PRIMARY_BUTTON_STYLE}
					loading={checking}
					disabled={
						!categoryConfig ||
						parsed.valid.length === 0 ||
						tooManyIds ||
						checking
					}
					onClick={handleCheck}
				>
					{copy?.check ?? "Check batch"}
				</Button>
			</div>

			{/*
				A batch that mixes local and international orders — or international orders
				bound for different countries — is refused outright: the responsible user and
				the status pair both depend on which single kind it is. The breakdown is shown
				so the operator can split the ids rather than guess where the seam is.
			*/}
			{preflightError && (
				<div className="flex flex-col gap-3">
					<Alert
						type="error"
						showIcon
						message={
							copy?.mixedTitle ?? "This batch cannot be processed"
						}
						description={preflightError.message}
					/>
					{preflightError.groups.length > 0 && (
						<div className="rounded-lg border border-gray-200 p-3 flex flex-col gap-1">
							<span className="font-medium text-sm">
								{copy?.mixedBreakdown ??
									"What the batch contains"}
							</span>
							{preflightError.groups.map((group) => (
								<span
									key={`${group.originCountryId}-${group.destinationCountryId}`}
									className="text-sm text-gray-600"
								>
									{group.originCountryName ??
										group.originCountryId ??
										"?"}{" "}
									→{" "}
									{group.destinationCountryName ??
										group.destinationCountryId ??
										"?"}{" "}
									— {group.orderCount}
								</span>
							))}
						</div>
					)}
				</div>
			)}

			{preflight && (
				<div className="flex flex-col gap-3">
					<div className="rounded-lg border border-[#4F16C2] bg-[#4F16C2]/5 p-3 flex flex-col gap-2">
						<span className="text-xs uppercase tracking-wide text-gray-500">
							{copy?.batchTitle ?? "Batch"}
						</span>
						<span className="font-medium flex items-center gap-2 flex-wrap">
							<Tag
								color={
									preflight.isInternational ? "blue" : "green"
								}
							>
								{directionLabel}
							</Tag>
							{preflight.isInternational &&
								preflight.destinationCountryName && (
									<span className="text-sm">
										{copy?.destination ?? "Destination"}:{" "}
										{preflight.destinationCountryName}
									</span>
								)}
							<span className="text-sm text-gray-600">
								{common?.totalRequested ?? "Requested"}:{" "}
								{preflight.totalRequested}
							</span>
						</span>
					</div>

					<IdListSection
						title={common?.notFound ?? "Not found"}
						ids={preflight.notFoundOrderIds ?? []}
						tone="warning"
						copyLabel={common?.copy ?? "Copy"}
						copiedMessage={common?.copied ?? "Copied to clipboard"}
					/>

					<div>
						<Button
							size="large"
							danger
							type="primary"
							disabled={submitting}
							onClick={() => setModalOpen(true)}
						>
							{copy?.openModal ?? "Change status"}
						</Button>
					</div>
				</div>
			)}

			{preflight && (
				<StatusUpdateModal
					open={modalOpen}
					orderIds={parsed.valid}
					preflight={preflight}
					users={users}
					usersLoading={usersLoading}
					historyConfig={historyConfig}
					onCancel={() => setModalOpen(false)}
					onSubmit={handleModalSubmit}
				/>
			)}

			<ConfirmModal
				open={confirmOpen}
				operationLabel={categoryStrings?.label ?? "Bulk Status Updates"}
				orderCount={parsed.valid.length}
				detail={{
					label: copy?.confirmChange ?? "Change",
					value: confirmDetail,
				}}
				danger
				dangerWarning={
					copy?.dangerWarning ??
					"This rewrites the status of every order in the list. Status drives settlement, driver visibility and reports — it cannot be undone from this page."
				}
				confirmWord={confirmWord}
				onConfirmWordChange={setConfirmWord}
				submitting={submitting}
				onOk={handleConfirm}
				onCancel={closeConfirm}
			/>

			<ResultPanels result={result} errorResult={errorResult} />
		</div>
	);
};

export default StatusUpdates;
